import { el, clear } from "./dom";
import { randomChars } from "../shared/http";
import type { PollDef } from "../shared/types";
import { labeled } from "./datepicker";

export interface PollBuilder {
  root: HTMLElement;
  /** The raw, unfiltered structure, including a poll or option not yet filled in. The form decides
   * what a save may carry (`pollsForSave`), and its isDirty() compares this against the baseline so
   * an empty poll the organizer just added counts as a change. */
  getRawPolls(): PollDef[];
  setPolls(polls: PollDef[]): void;
}

export function mountPollBuilder(initial: PollDef[], onChange?: () => void): PollBuilder {
  let polls: PollDef[] = initial.map(clonePoll);
  const root = el("div", { class: "polls-builder" });
  const list = el("div");
  const add = el("button", { type: "button", text: "Add poll", "data-testid": "add-poll" });
  add.addEventListener("click", () => {
    if (polls.length >= 5) return;
    polls.push({
      id: randomChars(10),
      question: "",
      options: [
        { id: randomChars(10), label: "" },
        { id: randomChars(10), label: "" },
      ],
    });
    paint();
    onChange?.();
  });
  root.append(list, add);

  function paint(): void {
    clear(list);
    for (const poll of polls) {
      const box = el("div", { class: "poll-builder" });
      const q = el("input", { type: "text", maxlength: "80", "data-testid": "poll-question" });
      q.value = poll.question;
      q.addEventListener("input", () => {
        poll.question = q.value;
        onChange?.();
      });
      box.append(labeled("Question", q));
      for (const opt of poll.options) {
        const row = el("div", { class: "opt-row" });
        const oi = el("input", { type: "text", maxlength: "40", "data-testid": "poll-option" });
        oi.value = opt.label;
        oi.addEventListener("input", () => {
          opt.label = oi.value;
          onChange?.();
        });
        row.append(labeled("Option", oi));
        if (poll.options.length > 2) {
          const rm = el("button", { type: "button", text: "Remove option" });
          rm.addEventListener("click", () => {
            poll.options = poll.options.filter((o) => o.id !== opt.id);
            paint();
            onChange?.();
          });
          row.append(rm);
        }
        box.append(row);
      }
      const addOpt = el("button", { type: "button", text: "Add option" });
      addOpt.addEventListener("click", () => {
        if (poll.options.length >= 12) return;
        poll.options.push({ id: randomChars(10), label: "" });
        paint();
        onChange?.();
      });
      const rmPoll = el("button", { type: "button", text: "Remove poll" });
      rmPoll.addEventListener("click", () => {
        polls = polls.filter((p) => p.id !== poll.id);
        paint();
        onChange?.();
      });
      box.append(addOpt, rmPoll);
      list.append(box);
    }
  }

  paint();
  return {
    root,
    getRawPolls() {
      return polls.map(clonePoll);
    },
    setPolls(next: PollDef[]) {
      if (JSON.stringify(polls) === JSON.stringify(next)) return;
      polls = next.map(clonePoll);
      paint();
    },
  };
}

function clonePoll(p: PollDef): PollDef {
  return { id: p.id, question: p.question, options: p.options.map((o) => ({ ...o })) };
}
